/**
 * Expense claims.
 *
 * The second screen an Employee touches, and the second where the same route
 * shows two very different things: your own claims, or the clinic's. Nothing
 * here branches on the role to do that — the scope layer decides which rows
 * come back, and this file just renders them.
 *
 * A claim funded from a till has nothing to reimburse, because the money left
 * the float when it was spent. The screen says so rather than offering a button
 * that would pay twice.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface ClaimLine {
  line_no: number
  spent_on: string
  description: string
  amount_paise: number
  gl_account_id: string
  has_receipt: boolean
}

interface Claim {
  id: string
  reference: string
  claimant_id: string
  claimant_name: string
  purpose: string
  status: string
  total_paise: number
  paid_from: string | null
  is_out_of_pocket: boolean
  lines_without_a_receipt: number
  approval_state: string
  journal_entry_id: string | null
  reimbursed_on: string | null
  lines: ClaimLine[]
}

interface Account {
  id: string
  code: string
  name: string
}

interface BankAccount {
  id: string
  name: string
  kind: string
}

interface Owed {
  claimant_id: string
  claimant_name: string
  outstanding_paise: number
  claims: string[]
}

interface DraftLine {
  spent_on: string
  description: string
  amount: string
  gl_account_id: string
  has_receipt: boolean
}

const today = () => new Date().toISOString().slice(0, 10)
const BLANK = (): DraftLine => ({
  spent_on: today(),
  description: '',
  amount: '',
  gl_account_id: '',
  has_receipt: false,
})

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function Expenses() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['claims'] })
    void queryClient.invalidateQueries({ queryKey: ['owed-to-staff'] })
  }

  const claims = useQuery({
    queryKey: ['claims'],
    queryFn: () => api.get<Claim[]>('/expenses/claims'),
  })
  const heads = useQuery({
    queryKey: ['expense-heads'],
    queryFn: () => api.get<Account[]>('/expenses/heads'),
    enabled: can('exp.claim.create'),
  })
  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('org.bank_account.view'),
  })
  const owed = useQuery({
    queryKey: ['owed-to-staff'],
    queryFn: () => api.get<Owed[]>('/expenses/owed'),
    enabled: can('exp.claim.approve'),
  })

  const submit = useMutation({
    mutationFn: (id: string) => api.post<Claim>(`/expenses/claims/${id}/submit`),
    onSuccess: refresh,
  })
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post<Claim>(`/expenses/claims/${id}/decide?approve=${approve}`),
    onSuccess: refresh,
  })
  const post = useMutation({
    mutationFn: (id: string) => api.post<Claim>(`/expenses/claims/${id}/post`),
    onSuccess: refresh,
  })
  const reimburse = useMutation({
    mutationFn: ({ id, bank }: { id: string; bank: string }) =>
      api.post<Claim>(`/expenses/claims/${id}/reimburse`, { bank_account_id: bank }),
    onSuccess: refresh,
  })

  if (claims.isLoading) return <Loading what="Loading claims" />
  const rows = claims.data ?? []
  const opened = rows.find((c) => c.id === open)
  const payFrom = (banks.data ?? []).filter((b) => b.kind !== 'cash')

  return (
    <>
      <ErrorNote
        error={claims.error ?? submit.error ?? decide.error ?? post.error ?? reimburse.error}
      />

      {owed.data && owed.data.length > 0 && (
        <Card title="Owed to staff" hint="from the claims themselves, on every read" tight>
          <table>
            <thead>
              <tr>
                <th>Who</th>
                <th>Claims</th>
                <th className="num">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {owed.data.map((row) => (
                <tr key={row.claimant_id}>
                  <td>{row.claimant_name}</td>
                  <td className="mono muted">{row.claims.join(', ')}</td>
                  <td className="num">{formatInr(row.outstanding_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Claims" hint={`${rows.length}`} tight>
        {rows.length === 0 ? (
          <div className="empty">Nothing claimed yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Who</th>
                <th>What for</th>
                <th className="num">Amount</th>
                <th>Funded by</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((claim) => (
                <tr key={claim.id}>
                  <td className="mono">{claim.reference}</td>
                  <td>{claim.claimant_name}</td>
                  <td>
                    {claim.purpose}
                    {claim.lines_without_a_receipt > 0 && (
                      <div className="faint">
                        {claim.lines_without_a_receipt} line
                        {claim.lines_without_a_receipt === 1 ? '' : 's'} with no receipt
                      </div>
                    )}
                  </td>
                  <td className="num">{formatInr(claim.total_paise)}</td>
                  <td className="muted">
                    {claim.paid_from ?? <span className="faint">own money</span>}
                  </td>
                  <td>
                    <Pill value={claim.status} />
                    {claim.status === 'submitted' && (
                      <div className="faint">{claim.approval_state}</div>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="small"
                      onClick={() => setOpen(open === claim.id ? null : claim.id)}
                    >
                      Lines
                    </button>{' '}
                    {claim.status === 'draft' && can('exp.claim.submit') && (
                      <button className="small" onClick={() => submit.mutate(claim.id)}>
                        Submit
                      </button>
                    )}
                    {claim.status === 'submitted' && can('exp.claim.approve') && (
                      <>
                        <button
                          className="small primary"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: claim.id, approve: true })}
                        >
                          Approve
                        </button>{' '}
                        <button
                          className="small"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: claim.id, approve: false })}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {claim.status === 'submitted' && !can('exp.claim.approve') && (
                      <span className="faint">with a checker</span>
                    )}
                    {claim.status === 'approved' && can('exp.reimbursement.prepare') && (
                      <button
                        className="small primary"
                        disabled={post.isPending}
                        onClick={() => post.mutate(claim.id)}
                      >
                        Post
                      </button>
                    )}
                    {claim.status === 'posted' &&
                      claim.is_out_of_pocket &&
                      can('exp.reimbursement.prepare') &&
                      payFrom.length > 0 && (
                        <button
                          className="small primary"
                          disabled={reimburse.isPending}
                          onClick={() =>
                            reimburse.mutate({ id: claim.id, bank: payFrom[0].id })
                          }
                        >
                          Reimburse
                        </button>
                      )}
                    {claim.status === 'posted' && !claim.is_out_of_pocket && (
                      <span className="faint">paid from the till</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {opened && (
        <Card
          title={`${opened.reference} — ${opened.purpose}`}
          hint={opened.approval_state}
          actions={
            <button className="small" onClick={() => setOpen(null)}>
              Close
            </button>
          }
        >
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Spent on</th>
                <th>What</th>
                <th className="num">Amount</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {opened.lines.map((line) => (
                <tr key={line.line_no}>
                  <td className="muted">{line.line_no}</td>
                  <td className="muted date">{line.spent_on}</td>
                  <td>{line.description}</td>
                  <td className="num">{formatInr(line.amount_paise)}</td>
                  <td>
                    {line.has_receipt ? (
                      <span className="muted">yes</span>
                    ) : (
                      <span className="faint">none</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!opened.is_out_of_pocket && (
            <Note kind="info">
              This was paid from {opened.paid_from}, so the money has already left. Nobody
              is owed anything and there is nothing to reimburse.
            </Note>
          )}
          {opened.reimbursed_on && (
            <Note kind="ok">Reimbursed on {opened.reimbursed_on}.</Note>
          )}
        </Card>
      )}

      {can('exp.claim.create') && (
        <NewClaim
          accounts={heads.data ?? []}
          tills={(banks.data ?? []).filter((b) => b.kind === 'cash')}
          onDone={refresh}
        />
      )}
    </>
  )
}

function NewClaim({
  accounts,
  tills,
  onDone,
}: {
  accounts: Account[]
  tills: BankAccount[]
  onDone: () => void
}) {
  const [purpose, setPurpose] = useState('')
  const [paidFrom, setPaidFrom] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([BLANK()])

  const create = useMutation({
    mutationFn: () =>
      api.post('/expenses/claims', {
        purpose,
        paid_from_bank_account_id: paidFrom || null,
        lines: lines.map((l) => ({
          spent_on: l.spent_on,
          description: l.description,
          amount_paise: toPaise(l.amount),
          gl_account_id: l.gl_account_id,
          has_receipt: l.has_receipt,
        })),
      }),
    onSuccess: () => {
      setPurpose('')
      setLines([BLANK()])
      onDone()
    },
  })

  const update = (i: number, patch: Partial<DraftLine>) =>
    setLines(lines.map((l, j) => (i === j ? { ...l, ...patch } : l)))
  const total = lines.reduce((sum, l) => sum + toPaise(l.amount), 0)

  return (
    <Card title="Claim an expense" hint="what you spent, and what you spent it on">
      <ErrorNote error={create.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <div className="row">
          <Field label="What was it for">
            <input
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Taxi to the Nashik clinic and back"
              required
            />
          </Field>
          {tills.length > 0 && (
            <Field
              label="Paid with"
              caption="a till means the money has already left it"
            >
              <select value={paidFrom} onChange={(e) => setPaidFrom(e.target.value)}>
                <option value="">My own money</option>
                {tills.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>

        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 150 }}>Spent on</th>
              <th>What</th>
              <th style={{ width: 220 }}>Expense head</th>
              <th className="num" style={{ width: 130 }}>
                Amount (₹)
              </th>
              <th style={{ width: 80 }}>Receipt</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="date"
                    value={line.spent_on}
                    onChange={(e) => update(i, { spent_on: e.target.value })}
                    required
                  />
                </td>
                <td>
                  <input
                    value={line.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                    required
                  />
                </td>
                <td>
                  <SearchSelect
                    aria-label="Expense head"
                    value={line.gl_account_id}
                    onChange={(v) => update(i, { gl_account_id: v })}
                    required
                    placeholder="Choose…"
                    options={accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))}
                  />
                </td>
                <td>
                  <input
                    className="num"
                    value={line.amount}
                    onChange={(e) => update(i, { amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={line.has_receipt}
                    onChange={(e) => update(i, { has_receipt: e.target.checked })}
                  />
                </td>
                <td className="num">
                  {lines.length > 1 && (
                    <button
                      type="button"
                      className="small"
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="row">
          <div className="shrink">
            <button type="button" className="small" onClick={() => setLines([...lines, BLANK()])}>
              Add a line
            </button>
          </div>
          <div className="shrink">
            <span className="caption">Total</span> <strong>{formatInr(total)}</strong>
          </div>
          <div className="shrink">
            <button className="primary" type="submit" disabled={create.isPending}>
              Claim it
            </button>
          </div>
        </div>
      </form>
    </Card>
  )
}
